/**
 * WebDialog: Gemini-styled shared dialog shell for web.
 * Provides: scrim, container, header (back/title/close), scrollable content, optional footer.
 *
 * Responsive: centred dialog on desktop, bottom sheet at phone widths
 * (<768px) so it matches the native `BottomSheet` the app presents on mobile
 * (`components/onboarding/BottomSheet.web.tsx`): bottom-anchored, full width,
 * 55px top corners, grab handle, slide-up. Every prop behaves identically in
 * both modes: only the shell geometry changes.
 *
 * Usage:
 * ```tsx
 * <WebDialog title="Settings" onClose={close} maxWidth={500}>
 *   <YourContent />
 * </WebDialog>
 * ```
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useLocale } from '../../hooks/useLocale';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';

/** Mirrors `BottomSheet.web.tsx` — the sheet the same content gets on native. */
const SHEET_MAX_WIDTH = 480;
const SHEET_CORNER_RADIUS = 55;

/**
 * Shell parity for the 'large' size: below the workspace's 900px breakpoint
 * the content panel goes full-bleed (`global.css` @media (max-width: 899px)),
 * so the large dialog follows it into full-screen there.
 */
const LARGE_FULLSCREEN_QUERY = '(max-width: 899px)';

function useLargeDialogFullscreen(enabled: boolean): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(LARGE_FULLSCREEN_QUERY).matches;
  });

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(LARGE_FULLSCREEN_QUERY);
    const onChange = () => setMatches(mq.matches);
    // Re-read on mount: the first render may have happened before hydration.
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [enabled]);

  return enabled && matches;
}

interface WebDialogProps {
  /** Dialog title displayed in the centered header */
  title: string;
  /** Decorative mark before the title text (CV Health's heart). */
  titleIcon?: ReactNode;
  /** Optional subtitle displayed below the title */
  subtitle?: string;
  /** Keep a full item name visible in detail/confirmation dialogs. */
  wrapTitle?: boolean;
  /** Called when the user closes the dialog (X button, backdrop click, or Escape) */
  onClose: () => void;
  /** Optional back button handler: shows a left chevron when provided */
  onBack?: () => void;
  /**
   * Custom node for the header's leading slot, in place of the back chevron
   * (e.g. a completion ring). Takes precedence over `onBack`, and widens the
   * slot to the node's natural width instead of the 40px button column.
   */
  headerLeading?: ReactNode;
  /**
   * Custom node for the header's trailing slot, in place of the X (e.g. a
   * "Done" text button). The leading and trailing columns then share the
   * spare width equally, so the title stays centred whatever their widths.
   */
  headerTrailing?: ReactNode;
  /** Accessible name of the X button (default "Close"). */
  closeLabel?: string;
  /** Hide the trailing X while keeping the title grid centered. */
  showCloseButton?: boolean;
  /**
   * Sheet mode only: the grab handle becomes the close control with this
   * accessible name (a 44px target around the bar), for sheets whose design
   * has no X on phones (CV Health).
   */
  handleCloseLabel?: string;
  /** A 16px title (7px icon gap) instead of the sheet's 20px. */
  compactTitle?: boolean;
  /** Max width of the dialog container (default: 500) */
  maxWidth?: number;
  /**
   * Dialog sizing. 'default' keeps the content-hugging `maxWidth` dialog with
   * the phone-width bottom-sheet fallback. 'large' is the workspace dialog
   * (job wizard): a fixed `min(960px, 100vw - 48px)` x `min(90vh, 860px)`
   * card with 24px radius on `var(--bg)`, which goes full-screen (inset 0,
   * radius 0) below the shell's 900px panel breakpoint instead of becoming a
   * bottom sheet. Ignores `maxWidth`.
   */
  size?: 'default' | 'large';
  /**
   * 'large' only: hug the child's height, capped at the same
   * `min(90vh, 860px)`, instead of always drawing the full card. A child whose
   * first screen is short — the job wizard's brief step — otherwise trails
   * half a viewport of empty card under its content. The child's own scroller
   * still takes over once the content passes the cap.
   */
  fitContent?: boolean;
  /**
   * Hide the dialog's own header row (back/title/close) and content padding
   * when the child draws its entire chrome itself (the job wizard's top bar).
   * The child becomes a flex column filling the card and owns its scrolling;
   * `title` is still required and becomes the dialog's aria-label. Backdrop
   * click and Escape still call `onClose`.
   */
  hideHeader?: boolean;
  /** Dialog content */
  children: ReactNode;
  /** Whether content area should scroll (default: true) */
  scrollable?: boolean;
  /**
   * Let the content area's child fill the dialog height instead of dictating it.
   * Makes the content a flex column that may shrink below its content size, so a
   * child that sizes itself with `flex-basis` (the settings two-pane shell)
   * shrinks on short viewports instead of pushing the dialog into its own
   * scrollbar. Opt-in: without it the content area keeps its plain block layout.
   */
  contentFill?: boolean;
  /** Optional footer content rendered below the scrollable area */
  footer?: ReactNode;
  /** Controls visibility: when false, renders nothing (default: true) */
  visible?: boolean;
  /** Backdrop treatment. Use transparent when the underlying page must remain fully visible. */
  backdrop?: 'dimmed' | 'transparent';
  /**
   * Stacking level for the portal overlay (default: 1000: the standard dialog
   * layer). Raise this above the 9999 create-skill dialog layer when a dialog
   * can be summoned from inside another dialog (e.g. the upgrade modal), so it
   * paints on top instead of behind. Stays below the alert host (100000).
   */
  zIndex?: number;
}

const hoverIn = (e: React.MouseEvent) => {
  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--hover-strong)';
};
const hoverOut = (e: React.MouseEvent) => {
  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
};

export function WebDialog({
  title,
  titleIcon,
  subtitle,
  wrapTitle = false,
  onClose,
  onBack,
  headerLeading,
  headerTrailing,
  closeLabel = 'Close',
  showCloseButton = true,
  handleCloseLabel,
  compactTitle = false,
  maxWidth = 500,
  size = 'default',
  fitContent = false,
  hideHeader = false,
  children,
  scrollable = true,
  contentFill = false,
  footer,
  visible = true,
  backdrop = 'dimmed',
  zIndex = 1000,
}: WebDialogProps) {
  const { isRTL } = useLocale();
  const isLarge = size === 'large';
  const isMobileViewport = useIsMobileViewport();
  // The large size never degrades into the bottom sheet: it has its own
  // full-screen fallback below the shell's 900px breakpoint.
  const isSheet = !isLarge && isMobileViewport;
  const isFullscreen = useLargeDialogFullscreen(isLarge);
  const titleId = useId();
  const subtitleId = useId();
  // Portal target. Only available after mount on the client.
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalTarget(document.body);
    }
  }, []);

  if (!visible) return null;
  if (!portalTarget) return null;

  const dialog = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: isSheet ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex,
        // Large: the width/height formulas below carry their own margins.
        padding: isSheet || isLarge ? 0 : 16,
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      {/* Scrim */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            backdrop === 'transparent'
              ? 'transparent'
              : isLarge
                ? 'rgba(0,0,0,0.45)'
                : 'rgba(0,0,0,0.4)',
          // backdropFilter: 'blur(12px)',
          // WebkitBackdropFilter: 'blur(12px)',
        }}
        onClick={onClose}
      />

      {/* Container: 28px radius dialog, or bottom sheet at phone widths */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={hideHeader ? undefined : titleId}
        aria-label={hideHeader ? title : undefined}
        aria-describedby={!hideHeader && subtitle ? subtitleId : undefined}
        className={isSheet ? 'tabbio-web-sheet' : undefined}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: isLarge
            ? isFullscreen
              ? '100%'
              : 'min(960px, calc(100vw - 48px))'
            : '100%',
          maxWidth: isLarge ? 'none' : isSheet ? SHEET_MAX_WIDTH : maxWidth,
          height: isLarge
            ? isFullscreen
              ? '100dvh'
              : fitContent
                ? 'auto'
                : 'min(90vh, 860px)'
            : undefined,
          maxHeight: isLarge
            ? isFullscreen || !fitContent
              ? undefined
              : 'min(90vh, 860px)'
            : isSheet
              ? 'calc(100dvh - 32px)'
              : 'calc(100vh - 80px)',
          borderRadius: isLarge
            ? isFullscreen
              ? 0
              : 24
            : isSheet
              ? `${SHEET_CORNER_RADIUS}px ${SHEET_CORNER_RADIUS}px 0 0`
              : 28,
          background: isLarge ? 'var(--bg)' : 'var(--menu-bg)',
          boxShadow:
            isLarge && isFullscreen
              ? 'none'
              : isSheet
                ? 'var(--gem-sheet-shadow)'
                : 'var(--gem-dialog-shadow)',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          color: 'var(--text-primary)',
        }}
      >
        {/* Grab handle: sheet-only affordance, matches BottomSheet.web */}
        {isSheet && handleCloseLabel ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={handleCloseLabel}
            style={{
              flexShrink: 0,
              alignSelf: 'center',
              display: 'grid',
              placeItems: 'center',
              width: 88,
              height: 30,
              marginTop: 0,
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 52,
                height: 6,
                borderRadius: 40,
                marginTop: 12,
                background: 'var(--gem-sheet-handle)',
              }}
            />
          </button>
        ) : isSheet ? (
          <div
            aria-hidden="true"
            style={{
              flexShrink: 0,
              width: 52,
              height: 6,
              borderRadius: 40,
              alignSelf: 'center',
              marginTop: 12,
              background: 'var(--gem-sheet-handle)',
            }}
          />
        ) : null}

        {/* Header: 3-column grid: [back] [title] [close] */}
        {!hideHeader && (
        <div
          style={{
            flexShrink: 0,
            display: 'grid',
            gridTemplateColumns: headerTrailing
              ? 'minmax(40px, 1fr) minmax(0, auto) minmax(40px, 1fr)'
              : headerLeading
                ? 'auto 1fr 40px'
                : '40px 1fr 40px',
            alignItems: 'center',
            padding: isSheet ? '8px 16px 4px' : '16px 20px',
            // The sheet reads as one continuous surface (like BottomSheet.web),
            // so it drops the dialog's header rule.
            borderBottom: isSheet ? undefined : '1px solid var(--input-border)',
            columnGap: 8,
          }}
        >
          {/* Leading slot: custom node, else the optional back chevron */}
          {headerLeading ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {headerLeading}
            </div>
          ) : onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                border: 0,
                cursor: 'pointer',
                width: 40,
                height: 40,
                background: 'transparent',
                color: 'var(--text-primary)',
                transition: 'background-color 150ms',
              }}
              onMouseEnter={hoverIn}
              onMouseLeave={hoverOut}
            >
              <MaterialSymbol name={isRTL ? 'chevron_right' : 'chevron_left'} size={24} />
            </button>
          ) : (
            <div />
          )}

          {/* Title + optional subtitle: Gemini 22px/500 dialog-title */}
          <div style={{ textAlign: 'center', minWidth: 0 }}>
            <h2
              id={titleId}
              style={{
                fontFamily: wrapTitle ? 'inherit' : "'Google Sans', 'Google Sans Text', sans-serif",
                // 20px/-0.2 is BottomSheet.web's title scale.
                fontSize: compactTitle ? 16 : isSheet ? 20 : 22,
                letterSpacing: isSheet && !compactTitle ? -0.2 : undefined,
                fontWeight: 500,
                color: 'var(--text-primary)',
                margin: 0,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: wrapTitle ? 'normal' : 'nowrap',
                overflowWrap: wrapTitle ? 'anywhere' : undefined,
              }}
            >
              {titleIcon ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: compactTitle ? 7 : 8,
                    maxWidth: '100%',
                    verticalAlign: 'top',
                  }}
                >
                  <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0 }}>
                    {titleIcon}
                  </span>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {title}
                  </span>
                </span>
              ) : (
                title
              )}
            </h2>
            {subtitle && (
              <p
                id={subtitleId}
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: 'var(--text-tertiary)',
                  margin: '2px 0 0',
                  lineHeight: 1.4,
                }}
              >
                {subtitle}
              </p>
            )}
          </div>

          {/* Trailing slot: custom node, else the 40x40 close circle */}
          {headerTrailing ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              {headerTrailing}
            </div>
          ) : showCloseButton ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: 0,
                cursor: 'pointer',
                width: 40,
                height: 40,
                background: 'transparent',
                color: 'var(--text-secondary)',
                transition: 'background-color 150ms',
              }}
              onMouseEnter={hoverIn}
              onMouseLeave={hoverOut}
            >
              <MaterialSymbol name="close" size={20} />
            </button>
          ) : (
            <div />
          )}
        </div>
        )}

        {/* Content. With `hideHeader` the child owns the full card: no
            padding, no dialog scrolling — it lays out and scrolls itself. */}
        <div
          style={
            hideHeader
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : {
                  flex: 1,
                  overflowY: scrollable ? 'auto' : 'visible',
                  padding: isSheet
                    ? footer
                      ? '12px 20px 16px'
                      : `12px 20px calc(20px + env(safe-area-inset-bottom))`
                    : '20px 24px 24px',
                  ...(contentFill
                    ? { display: 'flex', flexDirection: 'column', minHeight: 0 }
                    : null),
                }
          }
        >
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div
            style={{
              flexShrink: 0,
              padding: isSheet
                ? `4px 20px calc(20px + env(safe-area-inset-bottom))`
                : '12px 20px',
              borderTop: isSheet ? undefined : '1px solid var(--input-border)',
              // Match BottomSheet.web's footer: a flex column so a stretched
              // child button actually fills the width instead of hugging start.
              ...(isSheet
                ? { display: 'flex', flexDirection: 'column' }
                : null),
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(dialog, portalTarget);
}
